import { useEffect, useRef, useState, ReactNode } from 'react'
import { Skeleton } from '@mui/material'
import { styled } from '@mui/material/styles'

type LazyImageProps = {
  src?: string | null
  alt: string
  className?: string
  fallback?: ReactNode
  variant?: 'rectangular' | 'circular'
}

type ImageProps = {
  $loaded: boolean
}

const Container = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.default,
}))

const LoadingSkeleton = styled(Skeleton)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  transform: 'none',
  borderRadius: 'inherit',
  zIndex: 1,
  backgroundColor: theme.palette.mode === 'dark' ? '#2E333A' : '#DADDE2',
}))

const Image = styled('img', {
  shouldForwardProp: (prop) => prop !== '$loaded',
})<ImageProps>(({ $loaded }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  opacity: $loaded ? 1 : 0,
  transition: 'opacity 0.3s ease',
  display: 'block',
}))

const Fallback = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.disabled,
  backgroundColor: theme.palette.background.default,
  fontWeight: 600,
  zIndex: 2,
}))

/**
 * IntersectionObserver-backed image loader that keeps layout stable,
 * shows a skeleton while loading, and swaps to fallback content on error.
 */
const LazyImage = ({
  src,
  alt,
  className,
  fallback,
  variant = 'rectangular',
}: LazyImageProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [shouldLoad, setShouldLoad] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const target = containerRef.current
    if (!target) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )

    observer.observe(target)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    setLoaded(false)
    setFailed(false)
  }, [src])

  const showImage = shouldLoad && !!src && !failed
  const showSkeleton = !!src && !loaded && !failed

  return (
    <Container ref={containerRef} className={className}>
      {showSkeleton && <LoadingSkeleton variant={variant} animation='wave' />}

      {showImage && (
        <Image
          src={src ?? undefined}
          alt={alt}
          loading='lazy'
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          $loaded={loaded}
        />
      )}

      {(failed || !src) && fallback && <Fallback>{fallback}</Fallback>}
    </Container>
  )
}

export default LazyImage
